/**
 * Sign-in state for the applicant portal (worklist task 149).
 *
 * THIS FILE USED TO STORE THE SESSION TOKEN, in localStorage or sessionStorage
 * depending on "Keep me signed in". Anything running on the page could read it
 * there, so one XSS — or one compromised dependency — handed over a working
 * session. The session now lives in an HttpOnly cookie the API sets, which
 * this code cannot read at all, and `withCredentials` on the API client is
 * what sends it.
 *
 * What remains is a NON-CREDENTIAL marker answering "does this browser think
 * it is signed in", used to decide what the UI offers before any request is
 * made. It grants nothing: the server never sees it and never trusts it, and
 * every protected response is authorised from the cookie alone. Someone who
 * sets this flag by hand gets a menu that links to pages the API then refuses.
 *
 * The token functions were removed rather than left as no-ops, so that every
 * call site had to be looked at instead of silently doing nothing.
 *
 * "Keep me signed in" is now the cookie's own lifetime, decided by the server.
 */
const SIGNED_IN_KEY = "isLoggedIn";

/**
 * Everything written about WHO is signed in. Cleared together, because
 * clearing only the marker left the header still rendering an avatar and a
 * name out of `userDetails` — the UI looked signed in while every request was
 * being refused.
 */
const IDENTITY_KEYS = [
  SIGNED_IN_KEY,
  "userDetails",
  "email",
  "beneficiaryName",
  "beneficiaryType",
  "currentApplicationId",
  "isQualified",
];

/** Same-tab notification. `storage` only fires in OTHER tabs, never the one that wrote. */
const AUTH_EVENT = "lgv:auth-changed";

function notifyAuthChanged() {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  } catch {
    // Non-fatal; the next render still reads the current value.
  }
}

/**
 * Subscribes to sign-in state changes. Returns an unsubscribe function.
 *
 * Both events are needed: the custom one for this tab, and `storage` so a
 * sign-out in another tab does not leave this one showing a signed-in header.
 */
export function subscribeAuthChange(callback) {
  const handler = () => callback();
  window.addEventListener(AUTH_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(AUTH_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

/**
 * Drops every trace of the signed-in user and tells the UI.
 *
 * Used on sign-out AND whenever the API refuses a request as unauthenticated.
 * A dead session must not leave a signed-in-looking page behind: the reported
 * symptom was arriving from the marketing site long after the session expired,
 * seeing the signed-in header, and being handed 401s underneath it.
 */
export function clearSession() {
  try {
    for (const key of IDENTITY_KEYS) localStorage.removeItem(key);
  } catch {
    // Non-fatal.
  }
  notifyAuthChanged();
}

export function isSignedIn() {
  try {
    return localStorage.getItem(SIGNED_IN_KEY) === "true";
  } catch {
    // Storage can throw in private mode or when blocked; treat it as signed
    // out rather than breaking rendering.
    return false;
  }
}

export function setSignedIn() {
  try {
    localStorage.setItem(SIGNED_IN_KEY, "true");
  } catch {
    // Non-fatal: the marker only decides what the UI offers.
  }
  notifyAuthChanged();
}

/**
 * Clears only the marker. Prefer clearSession() — this exists for the rare
 * case where the cached profile should survive, and leaves the UI able to
 * render a stale name if misused.
 */
export function clearSignedIn() {
  try {
    localStorage.removeItem(SIGNED_IN_KEY);
  } catch {
    // Non-fatal.
  }
  notifyAuthChanged();
}
